import type { OpenerGroupPodState } from '../../api/useOpenerGroupPods';

/**
 * Pure status/derivation helpers for the per-group opener pod controls
 * (GroupPodControls on the Entry page + the BucketPage rail dots). Kept
 * out of the components so the derivation matrix is unit-testable —
 * bulkScope.ts / groupFilter.ts pattern.
 */

export type GroupPodStatus = 'off' | 'provisioning' | 'running' | 'panicked';

/**
 * Derive the operator-facing status from the pod-state entry. The state
 * endpoint is fail-soft: `engineReady` may be omitted when the cluster is
 * unreachable — missing is treated as NOT ready (provisioning), never as
 * running. A group absent from the response renders as Off.
 */
export function deriveGroupPodStatus(
  state: Pick<OpenerGroupPodState, 'podsDesired' | 'engineReady'> | undefined
): GroupPodStatus {
  if (!state) return 'off';
  if (state.podsDesired === 'panicked') return 'panicked';
  if (state.podsDesired === 'off') return 'off';
  return state.engineReady === true ? 'running' : 'provisioning';
}

/**
 * Start is allowed from Off and from Panicked (hard panic has no lockout —
 * `start` flips 'panicked' → 'on'), but NEVER while the GLOBAL opener
 * panic lockout is armed: the control plane refuses group starts under
 * lockout, so the button must not offer them.
 */
export function canStartGroup(
  status: GroupPodStatus,
  globalLockoutArmed: boolean
): boolean {
  if (globalLockoutArmed) return false;
  return status === 'off' || status === 'panicked';
}

/** Stop (pause-and-drain) is always allowed while anything is desired on. */
export function canStopGroup(status: GroupPodStatus): boolean {
  return status !== 'off';
}

/** Panics (soft or hard) only make sense while the group is not Off. */
export function canPanicGroup(status: GroupPodStatus): boolean {
  return status !== 'off';
}

export const GROUP_POD_STATUS_LABELS: Record<GroupPodStatus, string> = {
  off: 'Off',
  provisioning: 'Provisioning',
  running: 'Running',
  panicked: 'Panicked'
};

export const GROUP_POD_STATUS_CHIP_COLORS: Record<
  GroupPodStatus,
  'default' | 'info' | 'success' | 'error'
> = {
  off: 'default',
  provisioning: 'info',
  running: 'success',
  panicked: 'error'
};

/** Theme palette key for the BucketPage rail status dot. */
export const GROUP_POD_STATUS_DOT_COLORS: Record<GroupPodStatus, string> = {
  off: 'grey.700',
  provisioning: 'info.main',
  running: 'success.main',
  panicked: 'error.main'
};
